import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChoiceGroup } from '@/components/ui/choice-group';
import { Fieldset } from '@/components/ui/field';
import { SelectField } from '@/components/ui/select-field';
import { TextField } from '@/components/ui/text-field';
import { FORM_ERROR, errorMessage } from '@/features/auth/use-auth-error';
import { ZIP_PATTERN } from '@/lib/validation';

import { InfoDialog, type InfoTopic } from '../info-dialogs';

import { CHOICE_ERROR, YES_NO_MAYBE, type StepProps } from './step-props';
import { useSaveStep } from './use-save-step';

interface WorkTypeForm {
  zipCode: string;
  profession: string;
  cnaCertified: boolean;
  primaryWorkType: 'local' | 'travel' | '';
  mileSelection: number;
  otherWorkTypeInterest: 'Yes' | 'No' | 'Maybe' | '';
}

const CNA_LABEL = 'Certified Nursing Assistant (CNA)';
const MILE_MARKS = ['0', '10', '20', '30', '40', '50+'];

const WORK_CARDS = [
  {
    value: 'local',
    title: 'Local Positions',
    bullets: [
      'Choose individual shifts or multi-week assignments',
      'Local to where you live',
      'Flexible to fit your schedule',
    ],
    topic: 'localPositions',
  },
  {
    value: 'travel',
    title: 'Travel Positions',
    bullets: [
      'Multi-month placements',
      'Housing stipend and other benefits provided',
      'Experience a new location while earning great pay',
    ],
    topic: 'travelPositions',
  },
] as const;

export function WorkTypeStep({ record, lookups, onNext }: StepProps) {
  const [info, setInfo] = useState<InfoTopic>();
  const save = useSaveStep('work_type', onNext);
  const professions = lookups.profession ?? [];
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
  } = useForm<WorkTypeForm>({
    defaultValues: {
      zipCode: record.zipcode ?? '',
      profession: record.profession.id == null ? '' : String(record.profession.id),
      cnaCertified: record.cnaCertified ?? false,
      primaryWorkType: record.primaryWorkType ?? '',
      mileSelection: record.mileSelection ?? 0,
      otherWorkTypeInterest: record.otherWorkTypeInterest ?? '',
    },
  });

  const [profession, workType, rawMiles] = useWatch({
    control,
    name: ['profession', 'primaryWorkType', 'mileSelection'],
  });
  const miles = rawMiles;
  const isCna = professions.find((p) => p.value === profession)?.label === CNA_LABEL;
  const isLocal = workType === 'local';

  const submit = (v: WorkTypeForm) => {
    save.mutate({
      zipCode: v.zipCode,
      profession: { id: Number(v.profession) },
      cnaCertified: isCna ? v.cnaCertified : null,
      primaryWorkType: v.primaryWorkType,
      otherWorkTypeInterest: v.otherWorkTypeInterest,
      ...(v.primaryWorkType === 'local' ? { mileSelection: v.mileSelection } : {}),
    });
  };

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;

  return (
    <div className="stack stack--loose">
      <div>
        <h1>Let&apos;s start by getting to know you...</h1>
        <hr className="divider" />
      </div>
      {save.isError && <Alert>{errorMessage(save.error)}</Alert>}
      {!save.isError && hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}

      <form
        className="stack stack--loose"
        noValidate
        onSubmit={(e) => void handleSubmit(submit)(e)}
      >
        <TextField
          id="step2-zipCode"
          label="What is your zip code?"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          hint="So we can match you with work near you."
          error={errors.zipCode?.message}
          {...register('zipCode', {
            required: 'Please provide a valid zip code.',
            pattern: { value: ZIP_PATTERN, message: 'Please provide a valid zip code.' },
          })}
        />
        <SelectField
          id="step2-profession"
          label="What is your profession?"
          labelSize="lg"
          options={professions}
          error={errors.profession?.message}
          {...register('profession', { required: 'Please select your professional title' })}
        />

        {isCna && (
          <Checkbox
            label="Check this box if you are a Certified Medication Aide or Certified Medication Tech in addition to your CNA license."
            {...register('cnaCertified')}
          />
        )}

        <Fieldset
          legend="What type of work are you interested in?"
          size="lg"
          error={errors.primaryWorkType?.message}
          errorId="primaryWorkType-error"
        >
          <div className="work-cards">
            {WORK_CARDS.map((card) => (
              <label key={card.value} className="work-card">
                <input
                  type="radio"
                  value={card.value}
                  {...register('primaryWorkType', { required: CHOICE_ERROR })}
                />
                <div className="work-card__title">{card.title}</div>
                <ul>
                  {card.bullets.map((b) => (
                    <li key={b}>{b}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="link-button work-card__more"
                  onClick={() => {
                    setInfo(card.topic);
                  }}
                >
                  Learn more
                </button>
              </label>
            ))}
          </div>
        </Fieldset>

        {isLocal && (
          <div className="field">
            <label htmlFor="step2-mileSelection" className="field__label">
              What is your preferred maximum driving distance to work? (miles){' '}
              <span className="range-value">{miles >= 50 ? '50+' : miles}</span>
            </label>
            <input
              id="step2-mileSelection"
              type="range"
              className="range"
              min={0}
              max={50}
              step={10}
              aria-label="Distance"
              {...register('mileSelection', { valueAsNumber: true })}
            />
            <div className="range-marks" aria-hidden="true">
              {MILE_MARKS.map((m) => (
                <span key={m}>{m}</span>
              ))}
            </div>
          </div>
        )}

        {workType !== '' && (
          <ChoiceGroup
            type="radio"
            name="otherWorkTypeInterest"
            legend={`In the future, would you also be open to ${isLocal ? 'travel' : 'local shift'} positions?`}
            size="lg"
            inline
            options={YES_NO_MAYBE}
            error={errors.otherWorkTypeInterest?.message}
            registration={register('otherWorkTypeInterest', { required: CHOICE_ERROR })}
          />
        )}

        <Button type="submit" busy={save.isPending} disabled={isLocal && miles === 0}>
          Save &amp; continue
        </Button>
      </form>

      <InfoDialog
        topic={info}
        onClose={() => {
          setInfo(undefined);
        }}
      />
    </div>
  );
}
